import { useMemo } from "react";

/**
 * Renders streaming markdown-ish analysis text:
 * - Splits content on "## FASE" headers
 * - Renders the leading signature line in dedicated style
 * - Detects the FASE 5 / Strategic Map block and renders as inverted table
 */

function parseStrategicMap(block) {
  // Expect lines like "- KEY: VALUE"
  const rows = [];
  for (const line of block.split(/\r?\n/)) {
    const m = line.match(/^[-•]\s*([A-ZÁÉÍÓÚÑa-záéíóúñ\s]+):\s*(.+)$/);
    if (m) {
      rows.push({ key: m[1].trim().toUpperCase(), value: m[2].trim() });
    }
  }
  return rows;
}

export default function AnalysisOutput({ text, streaming }) {
  const parts = useMemo(() => {
    if (!text) return { signature: "", phases: [], tail: "" };
    const lines = text.split("\n");
    let signature = "";
    let rest = text;
    if (lines[0] && lines[0].includes("GalascioDAS")) {
      signature = lines[0].trim();
      rest = lines.slice(1).join("\n").trimStart();
    }
    // Split by phase headers
    const regex = /^##\s*FASE\s*(\d)\s*[—\-–]\s*(.+)$/gm;
    const phases = [];
    let match;
    const indices = [];
    while ((match = regex.exec(rest)) !== null) {
      indices.push({ start: match.index, num: match[1], title: match[2].trim(), headerEnd: match.index + match[0].length });
    }
    let tail = "";
    if (indices.length === 0) {
      return { signature, phases: [], tail: rest };
    }
    for (let i = 0; i < indices.length; i++) {
      const cur = indices[i];
      const next = indices[i + 1];
      const content = rest.slice(cur.headerEnd, next ? next.start : rest.length).trim();
      phases.push({ num: cur.num, title: cur.title, content });
    }
    // detect any trailing text after last phase (deepening question)
    const lastPhase = phases[phases.length - 1];
    // try split the trailing offer from FASE 5
    const offerMatch = lastPhase.content.match(/(Si quieres,?\s*puedo analizar[\s\S]+)$/i);
    if (offerMatch) {
      tail = offerMatch[1].trim();
      lastPhase.content = lastPhase.content.slice(0, offerMatch.index).trim();
    }
    return { signature, phases, tail };
  }, [text]);

  if (!text) return null;

  return (
    <div className="bg-white border border-[#171717] p-6 md:p-10" data-testid="analysis-output">
      {parts.signature && (
        <div
          data-testid="analysis-signature"
          className="font-mono-tech text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#A3A3A3] mb-10 border-b border-[#E5E5E5] pb-4"
        >
          {parts.signature}
        </div>
      )}

      {parts.phases.length === 0 ? (
        <div className="font-sans-body text-base leading-relaxed whitespace-pre-wrap">
          <span className={streaming ? "blink-cursor" : ""}>{parts.tail}</span>
        </div>
      ) : (
        <div className="flex flex-col gap-12 mt-2">
          {parts.phases.map((p, i) => {
            const isMap = p.num === "5";
            if (isMap) {
              const rows = parseStrategicMap(p.content);
              return (
                <section
                  key={`phase-${p.num}`}
                  data-testid={`phase-${p.num}`}
                  className="phase-in bg-[#0A0A0A] text-white p-8 md:p-12 border-t-4 border-[#D90429] -mx-6 md:-mx-10"
                >
                  <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-3">
                    FASE 0{p.num}
                  </div>
                  <h3 className="font-serif-display text-3xl md:text-4xl font-bold uppercase mb-8">
                    {p.title}
                  </h3>
                  {rows.length > 0 ? (
                    <dl data-testid="strategic-map" className="border-t border-white/20">
                      {rows.map((r) => (
                        <div
                          key={r.key}
                          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-8 border-b border-white/20 py-4"
                        >
                          <dt className="font-mono-tech text-[10px] text-[#A3A3A3] uppercase tracking-widest md:w-64 shrink-0">
                            {r.key}
                          </dt>
                          <dd className="font-sans-body text-lg text-white leading-snug">
                            {r.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <div className="font-sans-body whitespace-pre-wrap">{p.content}</div>
                  )}
                </section>
              );
            }
            return (
              <section
                key={`phase-${p.num}`}
                data-testid={`phase-${p.num}`}
                className="phase-in border-l-2 border-[#171717] pl-6 py-2 relative"
              >
                <span className="absolute -left-[1.15rem] top-0 bg-[#FAFAFA] text-[#171717] font-mono-tech font-bold px-1 text-sm border border-[#171717]">
                  {p.num.padStart(2, "0")}
                </span>
                <h3 className="font-serif-display text-2xl md:text-3xl font-bold mb-4 uppercase tracking-tight">
                  {p.title}
                </h3>
                <div className="font-sans-body text-base leading-relaxed text-[#262626] whitespace-pre-wrap">
                  {p.content}
                  {streaming && i === parts.phases.length - 1 ? (
                    <span className="blink-cursor" />
                  ) : null}
                </div>
              </section>
            );
          })}

          {parts.tail && (
            <section className="border-t border-[#E5E5E5] pt-6 font-serif-display italic text-xl text-[#0A0A0A]" data-testid="deepening-question">
              {parts.tail}
            </section>
          )}
        </div>
      )}

      {streaming && parts.phases.length === 0 && (
        <span className="blink-cursor inline-block ml-1" />
      )}
    </div>
  );
}
